import { ApiError } from './api';

/**
 * Read the server's error CODE / STATUS off a rejected request.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS RATHER THAN A REGEX OVER `err.message`
 *
 * `ApiError` splits a `{ error: { code, message } }` envelope across separate
 * fields: `.code` holds `STORAGE_QUOTA_EXCEEDED`, `.message` holds the human
 * sentence ("Your Fias storage is full"). Testing the MESSAGE for a code
 * therefore never matches, and every tailored branch silently collapses into
 * its generic fallback — the user is told "the link could not be created" when
 * the real answer was "you are out of credits".
 *
 * The pattern kept passing review because tests construct
 * `new Error('409 SOME_CODE')`, which a message regex does match. Green tests,
 * dead branches. Assert against a real `ApiError`, or the test proves nothing
 * about production.
 *
 * `isApiErrorCode` keeps a message fallback because a few server paths DO
 * encode the marker in the text on purpose — `ConflictError` throws
 * `DAILY_LINK_LIMIT: …`, and `providers/image/stability.ts` deliberately embeds
 * `CONTENT_FILTERED` in its message (there is no envelope on that path). The
 * fallback costs nothing everywhere else.
 * ---------------------------------------------------------------------------
 */
export function apiErrorCode(err: unknown): string | undefined {
  if (err instanceof ApiError && err.code) return err.code;
  return undefined;
}

/** True when `err` carries `code`, checking the message only as a fallback. */
export function isApiErrorCode(err: unknown, code: string): boolean {
  if (apiErrorCode(err) === code) return true;
  const message = err instanceof Error ? err.message : String(err);
  return message.includes(code);
}

/**
 * The HTTP status of a rejected request, or undefined for a non-API failure.
 *
 * Prefer this over sniffing `err.message` for "404" / "not found": the status
 * is exact, while the message is prose the server is free to reword.
 */
export function apiErrorStatus(err: unknown): number | undefined {
  return err instanceof ApiError ? err.status : undefined;
}
